import { useState, useEffect, useRef, useCallback } from 'react';
import { Search, ChevronDown, Check, Loader2, X, Building2 } from 'lucide-react';
import { clsx } from 'clsx';
import { personasApiV2, generationApi } from '@/services/api';

export interface ClientOption {
  uid: string;
  name: string;
  personas?: string[];
}

interface ClientComboboxProps {
  value: string;
  onChange: (uid: string, client?: ClientOption) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /** If true, shows "All Clients" as first option */
  showAllOption?: boolean;
  /** Size variant */
  size?: 'sm' | 'md';
}

export function ClientCombobox({
  value,
  onChange,
  placeholder = 'Select a client...',
  disabled = false,
  className,
  showAllOption = false,
  size = 'md',
}: ClientComboboxProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [clients, setClients] = useState<ClientOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Load clients on mount
  useEffect(() => {
    loadClients();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadClients = async () => {
    setLoading(true);
    setError(null);
    try {
      // Use personas API — backed by NeuroGraph REST, returns proper client UIDs/slugs
      const response = await personasApiV2.listClients();
      const clientList = response.clients.map((c) => ({
        // client_uid is the slug (e.g. "skoutorganic") used by briefs and the agent
        uid: c.client_uid,
        name: c.client_name || formatClientName(c.client_uid),
        personas: [],
      }));
      clientList.sort((a, b) => a.name.localeCompare(b.name));
      setClients(clientList);
    } catch (err) {
      console.warn('Personas API failed, trying briefs fallback:', err);
      // Fallback: extract unique clients from briefs
      try {
        const response = await generationApi.listBriefs({ limit: 100 });
        const uniqueClients = new Map<string, ClientOption>();
        response.briefs.forEach((b: { client_uid: string }) => {
          if (b.client_uid && !uniqueClients.has(b.client_uid)) {
            uniqueClients.set(b.client_uid, {
              uid: b.client_uid,
              name: formatClientName(b.client_uid),
            });
          }
        });
        const clientList = Array.from(uniqueClients.values());
        clientList.sort((a, b) => a.name.localeCompare(b.name));
        setClients(clientList);
      } catch (fallbackErr) {
        console.error('Failed to load clients:', fallbackErr);
        setError('Failed to load clients');
      }
    } finally {
      setLoading(false);
    }
  };

  // Format client_uid to readable name (e.g., "skoutorganic" -> "Skout Organic")
  const formatClientName = (uid: string): string => {
    return uid
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/[-_]/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  // Filter clients based on search
  const filteredClients = clients.filter((client) => {
    if (!search.trim()) return true;
    const searchLower = search.toLowerCase();
    return (
      client.name.toLowerCase().includes(searchLower) ||
      client.uid.toLowerCase().includes(searchLower)
    );
  });

  // Get selected client display name
  const selectedClient = clients.find((c) => c.uid === value);
  const displayValue = value ? (selectedClient?.name || value) : '';

  const handleSelect = useCallback((client: ClientOption | null) => {
    if (client) {
      onChange(client.uid, client);
    } else {
      onChange('');
    }
    setSearch('');
    setIsOpen(false);
  }, [onChange]);

  const handleClear = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
    setSearch('');
  }, [onChange]);

  const sizeClasses = {
    sm: 'text-xs py-1.5 px-2.5',
    md: 'text-sm py-2 px-3',
  };

  return (
    <div ref={containerRef} className={clsx('relative', className)}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => {
          if (!disabled) {
            setIsOpen(!isOpen);
            if (!isOpen) {
              setTimeout(() => inputRef.current?.focus(), 0);
            }
          }
        }}
        disabled={disabled}
        className={clsx(
          'w-full flex items-center justify-between gap-2',
          'border border-slate-200 rounded-lg bg-white',
          'focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500',
          'transition-colors',
          sizeClasses[size],
          disabled && 'opacity-50 cursor-not-allowed bg-slate-50',
          isOpen && 'ring-2 ring-teal-500/20 border-teal-500',
        )}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Building2 size={size === 'sm' ? 12 : 14} className="text-slate-400 flex-shrink-0" />
          {loading ? (
            <span className="text-slate-400 flex items-center gap-1.5">
              <Loader2 size={12} className="animate-spin" />
              Loading...
            </span>
          ) : displayValue ? (
            <span className="truncate text-slate-900">{displayValue}</span>
          ) : (
            <span className="text-slate-400">{placeholder}</span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {value && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="p-0.5 text-slate-400 hover:text-slate-600 rounded"
            >
              <X size={size === 'sm' ? 12 : 14} />
            </button>
          )}
          <ChevronDown
            size={size === 'sm' ? 12 : 14}
            className={clsx(
              'text-slate-400 transition-transform',
              isOpen && 'rotate-180',
            )}
          />
        </div>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden">
          {/* Search Input */}
          <div className="p-2 border-b border-slate-100">
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                ref={inputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search clients..."
                className={clsx(
                  'w-full pl-8 pr-3 py-1.5 text-sm',
                  'border border-slate-200 rounded-md',
                  'focus:outline-none focus:ring-1 focus:ring-teal-500/30 focus:border-teal-500',
                )}
              />
            </div>
          </div>

          {/* Options List */}
          <div className="max-h-60 overflow-y-auto">
            {error ? (
              <div className="px-3 py-4 text-center">
                <p className="text-sm text-rose-500">{error}</p>
                <button
                  onClick={loadClients}
                  className="mt-2 text-xs text-teal-500 hover:underline"
                >
                  Retry
                </button>
              </div>
            ) : loading ? (
              <div className="px-3 py-4 flex items-center justify-center gap-2 text-sm text-slate-400">
                <Loader2 size={14} className="animate-spin" />
                Loading clients...
              </div>
            ) : filteredClients.length === 0 ? (
              <div className="px-3 py-4 text-center text-sm text-slate-400">
                {search ? 'No clients match your search' : 'No clients available'}
              </div>
            ) : (
              <>
                {showAllOption && (
                  <button
                    type="button"
                    onClick={() => handleSelect(null)}
                    className={clsx(
                      'w-full flex items-center gap-2 px-3 py-2 text-sm text-left',
                      'hover:bg-slate-50 transition-colors',
                      !value && 'bg-teal-50',
                    )}
                  >
                    <Building2 size={14} className="text-slate-400" />
                    <span className="flex-1 font-medium text-slate-600">All Clients</span>
                    {!value && <Check size={14} className="text-teal-500" />}
                  </button>
                )}
                {filteredClients.map((client) => (
                  <button
                    key={client.uid}
                    type="button"
                    onClick={() => handleSelect(client)}
                    className={clsx(
                      'w-full flex items-center gap-2 px-3 py-2 text-sm text-left',
                      'hover:bg-slate-50 transition-colors',
                      client.uid === value && 'bg-teal-50',
                    )}
                  >
                    <Building2 size={14} className="text-slate-400" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-slate-900 truncate">{client.name}</div>
                      {client.uid !== client.name && (
                        <div className="text-[10px] text-slate-400 font-mono truncate">
                          {client.uid}
                        </div>
                      )}
                    </div>
                    {client.personas && client.personas.length > 0 && (
                      <span className="text-[10px] text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">
                        {client.personas.length} personas
                      </span>
                    )}
                    {client.uid === value && <Check size={14} className="text-teal-500 flex-shrink-0" />}
                  </button>
                ))}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
